/**
 * Shared constants used across web and server packages
 */

/**
 * Default Kanban columns created when a new project is created
 * @deprecated Use TASK_STATUSES instead for v2 status-based tasks
 */
export const DEFAULT_KANBAN_COLUMNS = [
  { name: 'Todo', position: 0 },
  { name: 'Doing', position: 1 },
  { name: 'In Review', position: 2 },
  { name: 'Done', position: 3 },
] as const

/**
 * Type-safe access to default column names
 * @deprecated Use TaskStatus instead
 */
export type DefaultColumnName = (typeof DEFAULT_KANBAN_COLUMNS)[number]['name']

// ============================================================================
// V2 STATUS-BASED CONSTANTS
// ============================================================================

/**
 * Valid task status values
 */
export const TASK_STATUSES = ['todo', 'doing', 'in_review', 'done'] as const

// Note: TaskStatus type is defined in livestore/schema.ts to avoid circular dependencies

/**
 * Status definitions for task list display
 */
export const STATUS_COLUMNS = [
  {
    id: 'todo',
    name: 'Todo',
    status: 'todo' as const,
    position: 0,
  },
  {
    id: 'doing',
    name: 'Doing',
    status: 'doing' as const,
    position: 1,
  },
  {
    id: 'in_review',
    name: 'In Review',
    status: 'in_review' as const,
    position: 2,
  },
  {
    id: 'done',
    name: 'Done',
    status: 'done' as const,
    position: 3,
  },
] as const

/**
 * Type for status column definition
 */
export type StatusColumn = (typeof STATUS_COLUMNS)[number]

// ============================================================================
// PROJECT CATEGORIES
// ============================================================================

/**
 * Project category type
 */
export type ProjectCategory =
  | 'health'
  | 'relationships'
  | 'finances'
  | 'growth'
  | 'leisure'
  | 'spirituality'
  | 'home'
  | 'contribution'

/**
 * Default project categories for UI display
 * These are hardcoded options - no database table needed
 *
 * Life Map Grid (2x4 layout):
 * Row 1: Health, Relationships, Finances, Growth
 * Row 2: Leisure, Spirituality, Home, Contribution
 */
export const PROJECT_CATEGORIES = [
  {
    value: 'health' as const,
    name: 'Health',
    description: 'Physical health, fitness, mental wellness, self-care',
    shortDescription: 'Body, mind, and wellbeing',
    colorHex: '#10B981',
    icon: '🧘‍♀️',
    sortOrder: 1,
  },
  {
    value: 'relationships' as const,
    name: 'Relationships',
    description: 'Family, friends, romantic relationships, social connections',
    shortDescription: 'Bonds with family and friends',
    colorHex: '#EC4899',
    icon: '💗',
    sortOrder: 2,
  },
  {
    value: 'finances' as const,
    name: 'Finances',
    description: 'Income, expenses, investments, financial planning',
    shortDescription: 'Managing and growing your money',
    colorHex: '#3B82F6',
    icon: '💰',
    sortOrder: 3,
  },
  {
    value: 'growth' as const,
    name: 'Learning',
    description: 'Education, skills, personal development, hobbies',
    shortDescription: 'Skills, knowledge, personal growth',
    colorHex: '#8B5CF6',
    icon: '🧠',
    sortOrder: 4,
  },
  {
    value: 'leisure' as const,
    name: 'Leisure',
    description: 'Recreation, entertainment, travel, fun activities',
    shortDescription: 'Travel, hobbies, and play',
    colorHex: '#F59E0B',
    icon: '🌴',
    sortOrder: 5,
  },
  {
    value: 'spirituality' as const,
    name: 'Purpose',
    description: 'Religion, philosophy, purpose, values, mindfulness',
    shortDescription: 'Philosophy, spirituality, meaning',
    colorHex: '#6366F1',
    icon: '🔮',
    sortOrder: 6,
  },
  {
    value: 'home' as const,
    name: 'Home',
    description: 'Living space, organization, home projects, environment',
    shortDescription: 'Your living space and environment',
    colorHex: '#14B8A6',
    icon: '🏡',
    sortOrder: 7,
  },
  {
    value: 'contribution' as const,
    name: 'Service',
    description: 'Community service, volunteering, giving back, impact',
    shortDescription: 'Giving back to others',
    colorHex: '#EF4444',
    icon: '🌍',
    sortOrder: 8,
  },
] as const

/**
 * Helper to get category display information
 */
export function getCategoryInfo(category: ProjectCategory | null | undefined) {
  if (!category) return null
  return PROJECT_CATEGORIES.find(c => c.value === category) || null
}
